/* eslint-disable no-unused-vars */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Paper,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  CircularProgress,
  Box,
  TextField,
  colors,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import PropTypes from "prop-types";
import { API } from "../../apis/api";
import DocumentViewerModal from "../DocumentViewerModal/DocumentViewerModal";
import {
  getChecklistMappings,
  buildDocumentEntries,
  buildAvailableDocTypes,
} from "../../pages/IEPFReport/initialState";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: "#f9f9f9",
    boxShadow:
      "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px",
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: "#04286F",
    fontWeight: 500,
  },
  formControl: {
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  addButton: {
    margin: theme.spacing(2, 0),
    background: "linear-gradient(90deg, #0A2D71 0%, #133984 100%)",
  },
  documentEntry: {
    position: "relative",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    "&.uploading": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  requiredLabel: {
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: "0.75rem",
    padding: theme.spacing(0.25, 0.75),
    borderRadius: theme.spacing(0.5),
    fontWeight: 500,
  },
  deleteButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  fileInput: {
    display: "none",
  },
  fileLabel: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  uploadButton: {
    position: "relative",
  },
  fileStatus: {
    display: "flex",
    alignItems: "flex-start",
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: "wrap", // Allow wrapping for long content
    minHeight: "32px", // Maintain consistent height
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
    flexWrap: "wrap",
  },
  uploadedChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  uploadingChip: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  errorChip: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  fileName: {
    maxWidth: "200px",
    whiteSpace: "break-spaces",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5), // Reduced gap for tighter alignment
    flexWrap: "wrap",
    alignItems: "center", // Align to top instead of center
    justifyContent: "flex-start",
    minHeight: "40px",
  },
  confirmButton: {
    color: theme.palette.success.main,
  },
  pendingUpload: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  disabledFileInput: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  completionStatus: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
  },
  customDocumentName: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  lockedDropdown: {
    "& .MuiSelect-root": {
      opacity: 0.8,
      color: colors.grey[900],
    },
  },
  viewButton: {
    color: theme.palette.info.main,
  },
  statusSummary: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  validStatus: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  invalidStatus: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  // New styles for file display
  fileDisplay: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
  },
  fileDisplayName: {
    flex: 1,
    marginRight: theme.spacing(1),
    fontSize: "0.875rem",
    wordBreak: "break-word",
  },
  fileRemoveButton: {
    color: theme.palette.error.main,
  },
}));

// Fallback document types with required ones marked
const FALLBACK_DOCUMENT_TYPES = [
  { label: "PAN Card", value: "pan_card", required: true },
  { label: "Aadhaar Card", value: "aadhar_card", required: true },
  { label: "Indemnity Bond", value: "indemnity_bond", required: true },
  { label: "Share Certificate", value: "share_certificate", required: true },
  { label: "Voter ID", value: "voter_id", required: false },
  { label: "Cancelled Cheque", value: "cancelled_cheque", required: false },
  { label: "Passport", value: "passport", required: false },
  { label: "Bank Statement", value: "bank_statement", required: false },
  { label: "Other", value: "other", required: false },
];

const RequiredDocuments = ({
  documentEntries,
  setDocumentEntries,
  handleDocumentChange,
  handleDocumentFileChange,
  handleDocumentUpload,
  handleAddDocument,
  handleRemoveDocument,
  getDocumentDownloadUrl,
  checklistId,
  onValidationChange, // New prop to communicate validation status
  disabled = false,
}) => {
  const classes = useStyles();
  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [isLoadingDocTypes, setIsLoadingDocTypes] = useState(false);
  const [documentViewerModal, setDocumentViewerModal] = useState({
    open: false,
    documentUrl: null,
    documentName: "",
    documentType: "",
    isLoading: false,
    error: null,
  });

  const userId = useSelector((state)=>state.auth.user.userId)
  // Fetch document types from API

  useEffect(() => {
    const fetchChecklistDocuments = async () => {
      if (!checklistId) return;

      try {
        setIsLoadingDocTypes(true);
        setDocumentEntries([]); // clear old docs

        const mappings = await getChecklistMappings(checklistId,userId);

        const entries = buildDocumentEntries(mappings);
        setDocumentEntries(entries);

        const docTypes = buildAvailableDocTypes(mappings);
        setAvailableDocumentTypes(docTypes);
      } catch (error) {
        console.error("Failed to fetch checklist mappings:", error);
        setAvailableDocumentTypes(FALLBACK_DOCUMENT_TYPES);
      } finally {
        setIsLoadingDocTypes(false);
      }
    };

    fetchChecklistDocuments();
  }, [checklistId]); // Re-fetch when checklistId changes

  // Handle document viewing
  const handleViewDocument = async (document) => {
    console.log("Viewing document:", document);
    if (!document.documentCode) return;

    setDocumentViewerModal({
      open: true,
      documentUrl: null,
      documentName:
        document.fileName ||
        document.file?.name ||
        `${document.documentType} Document`,
      documentType: document.documentType,
      isLoading: true,
      error: null,
    });

    try {
      const url = await getDocumentDownloadUrl(document.documentType,userId);
      if (url) {
        setDocumentViewerModal((prev) => ({
          ...prev,
          documentUrl: url,
          isLoading: false,
        }));
      } else {
        setDocumentViewerModal((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load document",
        }));
      }
    } catch (error) {
      setDocumentViewerModal((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to load document",
      }));
    }
  };

  const handleCloseDocumentViewer = () => {
    setDocumentViewerModal({
      open: false,
      documentUrl: null,
      documentName: "",
      documentType: "",
      isLoading: false,
      error: null,
    });
  };

  // Handle file deselection
  // Handle file deselection
  const handleFileDeselect = (index) => {
    console.log("Deselecting file at index:", index);
    console.log("Current document:", documentEntries[index]);

    // Clear the file by calling handleDocumentChange directly
    handleDocumentChange(index, "file", null);

    // Also clear any related file properties
    handleDocumentChange(index, "fileName", null);

    // If the parent expects handleDocumentFileChange to be called,
    // create an empty FileList or use null/undefined
    try {
      handleDocumentFileChange(index, null);
    } catch (e) {
      console.log(
        "handleDocumentFileChange with null failed, trying empty array"
      );
      try {
        handleDocumentFileChange(index, []);
      } catch (e2) {
        console.log("Both approaches failed:", e, e2);
      }
    }
  };
  // Get required document types
  const requiredDocumentTypes = availableDocumentTypes.filter(
    (doc) => doc.required
  );
  const optionalDocumentTypes = availableDocumentTypes.filter(
    (doc) => !doc.required
  );

  // Check validation status
  const getValidationStatus = () => {
    const requiredUploaded = requiredDocumentTypes.every((reqDoc) =>
      documentEntries.some(
        (entry) => entry.documentType === reqDoc.value && entry.documentCode
      )
    );

    const hasUploading = documentEntries.some((doc) => doc.isUploading);
    const hasPendingUploads = documentEntries.some(
      (doc) => doc.file && !doc.documentCode && !doc.isUploading
    );

    return {
      isValid: requiredUploaded && !hasUploading && !hasPendingUploads,
      requiredUploaded,
      hasUploading,
      hasPendingUploads,
      totalRequired: requiredDocumentTypes.length,
      uploadedRequired: requiredDocumentTypes.filter((reqDoc) =>
        documentEntries.some(
          (entry) => entry.documentType === reqDoc.value && entry.documentCode
        )
      ).length,
    };
  };

  // Communicate validation status to parent
  useEffect(() => {
    if (onValidationChange) {
      const status = getValidationStatus();
      onValidationChange(status.isValid);
    }
  }, [documentEntries, availableDocumentTypes, onValidationChange]);

  // Get selected document types to prevent duplicates
  const getSelectedDocumentTypes = () =>
    documentEntries
      .map((entry) => entry.documentType)
      .filter((type) => type && type !== "");

  // Get available options for a specific entry
  const getAvailableOptions = (currentIndex) => {
    const selectedTypes = getSelectedDocumentTypes();
    const currentValue = documentEntries[currentIndex]?.documentType;

    // If the currentValue is not in availableDocumentTypes, treat it as 'other'
    const isCurrentValueValid = availableDocumentTypes.some(
      (docType) => docType.value === currentValue
    );
    if (!isCurrentValueValid && currentValue) {
      // If not already present, add a temporary 'Other' option for this value
      return [
        ...availableDocumentTypes.filter(
          (docType) => docType.value !== "other"
        ),
        { label: `Other (${currentValue})`, value: "other", required: false },
      ];
    }

    // Allow 'other' to be selected multiple times
    return availableDocumentTypes.filter(
      (docType) =>
        docType.value === "other" || // Always allow 'other'
        !selectedTypes.includes(docType.value) ||
        docType.value === currentValue
    );
  };

  // If documentType is not in availableDocumentTypes, treat as 'other' for display
  const getDisplayDocumentType = (documentType) => {
    const found = availableDocumentTypes.find(
      (doc) => doc.value === documentType
    );
    if (found) return documentType;
    if (documentType) return "other";
    return "";
  };

  const getFileStatus = (document) => {
    if (document.isUploading) {
      return (
        <Chip
          icon={<CircularProgress size={16} />}
          label="Uploading..."
          className={classes.uploadingChip}
          size="small"
        />
      );
    }

    if (document.file && document.documentCode) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Uploaded Successfully"
          className={classes.uploadedChip}
          size="small"
        />
      );
    }

    if (document.documentCode) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Document Available"
          className={classes.uploadedChip}
          size="small"
        />
      );
    }

    if (document.file && !document.documentCode && !document.isUploading) {
      return (
        <Chip
          icon={<WarningIcon />}
          label="Pending Upload"
          className={classes.pendingUpload}
          size="small"
        />
      );
    }

    return null;
  };

  // Check if document type is required
  const isRequiredDocument = (documentType) =>
    requiredDocumentTypes.some((req) => req.value === documentType);

  // Check if delete should be shown
  const shouldShowDelete = (document, index) => {
    if (isRequiredDocument(document.documentType)) {
      // For required documents, only show delete if file has been uploaded
      return document.documentCode !== null;
    }
    // For optional documents, always show delete
    return documentEntries.length > requiredDocumentTypes.length;
  };

  // Enhanced remove handler
  const handleEnhancedRemoveDocument = (index) => {
    const document = documentEntries[index];
    if (isRequiredDocument(document.documentType)) {
      handleRemoveDocument(index, true);
    } else {
      handleRemoveDocument(index);
    }
  };

  // Handle custom document name change
  const handleCustomDocumentNameChange = (index, value) => {
    handleDocumentChange(index, "customDocumentName", value);
  };

  // Check if dropdown should be disabled
  const isDropdownDisabled = (document, index) => {
    const isRequired = isRequiredDocument(document.documentType);
    return (
      document.isUploading ||
      document.documentCode ||
      (isRequired && document.documentType) // Lock required documents once selected
    );
  };

  const validationStatus = getValidationStatus();

  if (isLoadingDocTypes) {
    return (
      <Paper className={classes.paper} elevation={2}>
        <Box display="flex" alignItems="center" justifyContent="center" p={3}>
          <CircularProgress size={24} style={{ marginRight: 16 }} />
          <Typography>Loading document types...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={2}>
      <Typography variant="h6" className={classes.sectionTitle}>
        Required Documents
      </Typography>

      {documentEntries.map((document, index) => {
        const isRequired = isRequiredDocument(document.documentType);
        const availableOptions = getAvailableOptions(index);
        const canUploadFile =
          document.documentType &&
          !document.documentCode &&
          !document.isUploading &&
          (document.documentType !== "other" ||
            document.customDocumentName?.trim());
        const canChooseFile = document.documentType && !document.documentCode;
        const dropdownDisabled = isDropdownDisabled(document, index);
        const isOtherDocument = document.documentType === "other";
        const hasExistingDocument = document.documentCode && !document.file;

        return (
          <div
            key={index}
            className={`${classes.documentEntry} ${
              document.isUploading ? "uploading" : ""
            } ${isRequired ? "required" : ""}`}
          >
            {isRequired && (
              <div className={classes.requiredLabel}>REQUIRED</div>
            )}

            <Grid container spacing={2} alignItems="flex-start">
              {/* Document Type Selection */}
              <Grid item xs={12} md={5}>
                <FormControl
                  variant="outlined"
                  className={`${classes.formControl} ${
                    dropdownDisabled ? classes.lockedDropdown : ""
                  }`}
                >
                  <InputLabel id={`document-type-label-${index}`}>
                    Document Type {isRequired && "*"}
                  </InputLabel>
                  <Select
                    labelId={`document-type-label-${index}`}
                    id={`document-type-${index}`}
                    value={getDisplayDocumentType(document.documentType)}
                    onChange={(e) =>
                      handleDocumentChange(
                        index,
                        "documentType",
                        e.target.value
                      )
                    }
                    label={`Document Type ${isRequired ? "*" : ""}`}
                    fullWidth
                    disabled={disabled || dropdownDisabled}
                    required={isRequired}
                  >
                    {availableOptions.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label} {type.required && "(Required)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* 
                {dropdownDisabled && isRequired && (
                  <Typography variant="caption" color="textSecondary">
                    Required document type cannot be changed
                  </Typography>
                )} */}

                {/* Custom Document Name Field for "Other" option */}
                {isOtherDocument && (
                  <TextField
                    className={classes.customDocumentName}
                    variant="outlined"
                    size="small"
                    label="Document Name *"
                    placeholder="Enter custom document name"
                    value={document.customDocumentName || ""}
                    onChange={(e) =>
                      handleCustomDocumentNameChange(index, e.target.value)
                    }
                    disabled={
                      disabled || document.isUploading || document.documentCode
                    }
                    required
                    error={
                      isOtherDocument && !document.customDocumentName?.trim()
                    }
                    helperText={
                      isOtherDocument && !document.customDocumentName?.trim()
                        ? "Please enter document name"
                        : ""
                    }
                  />
                )}
              </Grid>

              {/* File Selection */}
              <Grid item xs={12} md={5}>
                {/* Show file display if file is selected */}
                {document.file || document.documentCode ? (
                  <div className={classes.fileStatus}>
                    <div className={classes.fileInfo}>
                      <Typography variant="body1" className={classes.fileName}>
                        {document.file
                          ? document.file.name
                          : hasExistingDocument
                          ? document.fileName || "Existing document"
                          : "No file chosen"}
                      </Typography>

                      {/* Show file status chip */}
                      {getFileStatus(document)}
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      className={classes.fileInput}
                      id={`file-${index}`}
                      type="file"
                      onChange={(e) => {
                        handleDocumentFileChange(index, e.target.files);
                        // Reset the input value to allow selecting the same file again
                        e.target.value = "";
                        e.target.files = null;
                      }}
                      disabled={disabled || !canChooseFile}
                    />
                    <label
                      htmlFor={`file-${index}`}
                      className={`${classes.fileLabel} ${
                        !canChooseFile ? classes.disabledFileInput : ""
                      }`}
                    >
                      <Button
                        variant="outlined"
                        component="span"
                        className={classes.button}
                        disabled={disabled || !canChooseFile}
                        color={document.documentCode ? "primary" : "default"}
                      >
                        {document.isUploading ? (
                          <>
                            <CircularProgress
                              size={16}
                              style={{ marginRight: 8 }}
                            />
                            Uploading...
                          </>
                        ) : document.documentCode ? (
                          hasExistingDocument ? (
                            "Replace File"
                          ) : (
                            "File Uploaded"
                          )
                        ) : (
                          "Choose File"
                        )}
                      </Button>
                    </label>
                  </>
                )}

                {/* File status and messages */}
                <div className={classes.fileStatus}>
                  {/* Show existing document name if available */}

                  {/* Show error messages */}
                  {!document.documentType && (
                    <Typography variant="caption" color="error">
                      Please select document type first
                    </Typography>
                  )}

                  {isOtherDocument && !document.customDocumentName?.trim() && (
                    <Typography variant="caption" color="error">
                      Please enter document name first
                    </Typography>
                  )}
                </div>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} md={2}>
                <div className={classes.actionButtons}>
                  {document.file && !document.documentCode && (
                    <IconButton
                      className={classes.fileRemoveButton}
                      onClick={() => handleFileDeselect(index)}
                      disabled={disabled || document.isUploading}
                      title="Remove file"
                    >
                      <CloseIcon fontSize="responsive" />
                    </IconButton>
                  )}

                  {canUploadFile && document.file && (
                    <IconButton
                      onClick={() => handleDocumentUpload(index)}
                      color="primary"
                      title="Upload Document"
                      className={classes.confirmButton}
                      disabled={disabled}
                    >
                      <CheckCircleIcon  fontSize="responsive" />
                    </IconButton>
                  )}

                  {document.documentCode && (
                    <IconButton
                      onClick={() => handleViewDocument(document)}
                      color="primary"
                      title="View Document"
                      className={classes.viewButton}
                      disabled={disabled}
                    >
                      <VisibilityIcon fontSize="responsive" />
                    </IconButton>
                  )}

                  {shouldShowDelete(document, index) && (
                    <IconButton
                      onClick={() => handleEnhancedRemoveDocument(index)}
                      disabled={disabled || document.isUploading}
                      color="secondary"
                      title={
                        document.isUploading
                          ? "Cannot delete while uploading"
                          : "Delete Document"
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
        );
      })}

      {/* Add Document Button */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddDocument}
        className={classes.addButton}
        disabled={
          disabled ||
          getSelectedDocumentTypes().length >= availableDocumentTypes.length
        }
      >
        Add Document
      </Button>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        open={documentViewerModal.open}
        onClose={handleCloseDocumentViewer}
        documentUrl={documentViewerModal.documentUrl}
        documentName={documentViewerModal.documentName}
        documentType={documentViewerModal.documentType}
        isLoading={documentViewerModal.isLoading}
        error={documentViewerModal.error}
      />
    </Paper>
  );
};

RequiredDocuments.propTypes = {
  documentEntries: PropTypes.array.isRequired,
  setDocumentEntries: PropTypes.func.isRequired,
  handleDocumentChange: PropTypes.func.isRequired,
  handleDocumentFileChange: PropTypes.func.isRequired,
  handleDocumentUpload: PropTypes.func.isRequired,
  handleAddDocument: PropTypes.func.isRequired,
  handleRemoveDocument: PropTypes.func.isRequired,
  getDocumentDownloadUrl: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  disabled: PropTypes.bool,
  checklistId: PropTypes.number,
};

RequiredDocuments.defaultProps = {
  onValidationChange: null,
  disabled: false,
};

export default RequiredDocuments;
